/**
 * Buildings BAU pathways: stock-based electrification model (residential vs commercial sales curves).
 * Natural gas therms = res×(1−eR×sR)+com×(1−eC×sC) using default end-use electrifiable shares eR,eC (space+water)
 * and per-sector turnover stock sR,sC — so 100% stock only eliminates **electrifiable** gas, not cooking/other.
 * Produces thermsByYear, kwhByYear, rngShareByYear for use as baseline in Policy Tool.
 * Aligns with CARB-style assumptions (sales electrification, turnover, RNG).
 *
 * Sales electrification curves: all scenarios use LINEAR ramps (share = linear in time to target).
 * Accelerated applies ACCELERATED_LOCAL_ADOPTION_SPEEDUP (local governments encourage adoption 25% faster)
 * to both sales timelines and stock turnover.
 * Choice: linear for transparency and consistency with policy year targets; a logistic/S-curve
 * could be added later for smoother adoption if desired.
 */

import {
  BuildingsEnergyData,
  calculateTotalNaturalGasConsumption,
  calculateTotalElectricityConsumption,
  getNaturalGasConsumptionBySector,
} from './buildingsEnergyDataService';
import { resolveBuildingEndUseAssumptions } from './buildingEndUseAssumptions';

export type BuildingsBauScenario = 'conservative' | 'current_policy' | 'accelerated';

export interface BuildingsBauPathway {
  years: number[];
  thermsByYear: number[];
  kwhByYear: number[];
  rngShareByYear: number[];
  /** Residential/commercial gas demand inputs are fixed at this year for all pathway years ≥ this year. */
  demandAnchorYear: number;
  /**
   * Aggregate BAU share (0–1) of **electrifiable** building gas (space + water) that is electrified each year.
   * Chart “electrified %” uses this; fossil line is the remainder of that pool (not total gas).
   */
  stockElectrifiedByYear?: number[];
  /** Remaining share (0–1) of the electrifiable gas pool not yet converted under BAU (= 1 − stockElectrified when both use pool semantics). */
  stockFossilByYear?: number[];
  salesElectrificationShareByYear?: number[];
  /** Turnover model: fraction of residential **heating stock** on electric track (0–1). Gas reduction uses electrifiableResidential × this. */
  stockElectrifiedResidentialByYear?: number[];
  stockElectrifiedCommercialByYear?: number[];
  /** Electrifiable gas fractions used to build therms (from default end-use assumptions). */
  electrifiableResidential?: number;
  electrifiableCommercial?: number;
}

/** Annual turnover rate: share of stock replaced each year (e.g. 6%). */
export const BUILDINGS_TURNOVER_RATE = 0.06;

/**
 * Accelerated BAU: local governments encourage adoption 25% faster than the base Accelerated path.
 * Applied as (1) turnover rate × 1.25 and (2) sales-electrification timelines compressed by 1.25.
 * Critical assumption — documented in Building Electrification → Critical Assumptions.
 */
export const ACCELERATED_LOCAL_ADOPTION_SPEEDUP = 1.25;

/** Bump when buildings BAU pathway math changes so caches are not reused stale. */
export const BUILDINGS_BAU_PATHWAY_CACHE_VERSION = `adopt${String(ACCELERATED_LOCAL_ADOPTION_SPEEDUP).replace('.', '')}`;

/** Effective stock turnover for a Buildings BAU scenario. */
export function turnoverRateForBauScenario(scenario: BuildingsBauScenario): number {
  if (scenario === 'accelerated') {
    return BUILDINGS_TURNOVER_RATE * ACCELERATED_LOCAL_ADOPTION_SPEEDUP;
  }
  return BUILDINGS_TURNOVER_RATE;
}

/** Heat pump coefficient of performance (consistent across codebase). */
export const HEAT_PUMP_COP = 3.5;

/** kWh per therm (thermal). */
export const KWH_PER_THERM = 29.3;

const NG_EMISSION_FACTOR_KG_PER_THERM = 5.29;

/**
 * Initial electrified stock share in the base year (2023).
 * Used as the starting point for stock evolution: stockElectrified[baseYear] = this value.
 * Assumption: small share of building heating/cooking stock is already electric in 2023 (~5%).
 * Can be refined with data (e.g. from EIA or utility mix) if available.
 */
export const INITIAL_STOCK_ELECTRIFIED_BASE_YEAR = 0.05;

/** RNG share (0–1) in 2045 by scenario. Used in UI and emissions. */
export const RNG_SHARE_2045_BY_SCENARIO: Record<BuildingsBauScenario, number> = {
  conservative: 0.1,
  current_policy: 0.3,
  accelerated: 0.32,
};

/**
 * BAU trajectory assumption: existing buildings become this much more efficient (combined fuels) by 2045,
 * linear from 2023, when the policy toggle is on.
 */
export function bauExistingStockEfficiencyPercentBy2045(scenario: BuildingsBauScenario): number {
  switch (scenario) {
    case 'conservative':
      return 0;
    case 'current_policy':
      return 10;
    case 'accelerated':
      return 20;
    default:
      return 10;
  }
}

/**
 * Baseline therms = no-electrification counterfactual (first-order approximation).
 * We use OBSERVED natural gas consumption from buildings data as the fossil demand series.
 * This is an approximation: observed data may already reflect some electrification, so true
 * "no electrification" therms could be higher. Refinement (e.g. sector-level or survey-based
 * counterfactual) may be done later.
 */
function getBaselineThermsNoElectrification(
  rawBuildingsData: BuildingsEnergyData,
  years: number[]
): number[] {
  return years.map((y) => {
    const v = calculateTotalNaturalGasConsumption(rawBuildingsData, y);
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
}

/**
 * Baseline building electricity before adding electrification load (first-order approximation).
 * We use OBSERVED electricity consumption as the non-electrification trend (plug load, cooling, etc.);
 * electrification adder is added separately in the model.
 * This is an approximation: observed kWh may already include some heat-pump load. May be refined later.
 */
function getBaselineTrendKwh(
  rawBuildingsData: BuildingsEnergyData,
  years: number[]
): number[] {
  return years.map((y) => {
    const v = calculateTotalElectricityConsumption(rawBuildingsData, y);
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
}

/**
 * Electric **sales** share (0–1) by year, separate residential vs commercial turnover drivers.
 * - Conservative: both sectors cap at 50% sales by 2045.
 * - Current policy: residential 100% electric new sales by 2035; commercial 100% by 2045.
 * - Accelerated: both reach 100% by 2035 on the base path, then timelines are compressed by
 *   ACCELERATED_LOCAL_ADOPTION_SPEEDUP (local governments encourage adoption 25% faster).
 */
function getSalesElectrificationShareByYearSplit(
  scenario: BuildingsBauScenario,
  years: number[],
  baseYear: number,
  _targetYear: number
): { res: number[]; com: number[] } {
  const res: number[] = [];
  const com: number[] = [];
  for (const y of years) {
    if (y <= baseYear) {
      res.push(0);
      com.push(0);
      continue;
    }
    switch (scenario) {
      case 'conservative': {
        const endYear = 2045;
        const t = Math.min(1, (y - baseYear) / (endYear - baseYear));
        const v = t * 0.5;
        res.push(v);
        com.push(v);
        break;
      }
      case 'current_policy': {
        const resShare = y >= 2035 ? 1 : Math.min(1, (y - baseYear) / (2035 - baseYear));
        const comShare = Math.min(1, (y - baseYear) / (2045 - baseYear));
        res.push(resShare);
        com.push(comShare);
        break;
      }
      case 'accelerated': {
        // Base Accelerated: 100% electric sales by 2035 for both sectors; then 25% faster locally.
        const baseEndYear = 2035;
        const baseDuration = Math.max(1, baseEndYear - baseYear);
        const fastDuration = baseDuration / ACCELERATED_LOCAL_ADOPTION_SPEEDUP;
        const t = Math.min(1, (y - baseYear) / fastDuration);
        res.push(t);
        com.push(t);
        break;
      }
      default:
        res.push(0);
        com.push(0);
    }
  }
  return { res, com };
}

/** Blended sales curve (50/50 res/com) for tables that expect a single series — matches prior current_policy aggregate. */
function getBlendedSalesShareForDebug(
  scenario: BuildingsBauScenario,
  years: number[],
  baseYear: number,
  targetYear: number
): number[] {
  const { res, com } = getSalesElectrificationShareByYearSplit(scenario, years, baseYear, targetYear);
  return years.map((_, i) => (res[i] + com[i]) / 2);
}

/**
 * RNG share (0–1) by year. Uses RNG_SHARE_2045_BY_SCENARIO for 2045 target; linear ramp from base year.
 */
function getRngShareByYear(
  scenario: BuildingsBauScenario,
  years: number[],
  baseYear: number,
  targetYear: number
): number[] {
  const result: number[] = [];
  const rng2045 = RNG_SHARE_2045_BY_SCENARIO[scenario];
  for (const y of years) {
    if (y <= baseYear) {
      result.push(0);
      continue;
    }
    const t = Math.min(1, (y - baseYear) / (targetYear - baseYear));
    result.push(t * rng2045);
  }
  return result;
}

/**
 * Stock evolution (correct formula with fossil-remaining term):
 * stockElectrified[t] = stockElectrified[t-1] + turnoverRate * (1 - stockElectrified[t-1]) * salesElectrificationShare[t]
 * Clamp to [0, 1].
 */
function evolveStock(
  salesShareByYear: number[],
  turnoverRate: number,
  initialStockElectrified: number,
  years: number[],
  baseYear: number
): { stockElectrifiedByYear: number[]; stockFossilByYear: number[] } {
  const baseIdx = years.indexOf(baseYear);
  const stockElectrifiedByYear: number[] = years.map(() => 0);
  const stockFossilByYear: number[] = years.map(() => 1);

  for (let i = 0; i < years.length; i++) {
    if (i <= baseIdx) {
      stockElectrifiedByYear[i] = initialStockElectrified;
      stockFossilByYear[i] = 1 - initialStockElectrified;
      continue;
    }
    const prev = Math.max(0, Math.min(1, stockElectrifiedByYear[i - 1]));
    const salesShare = Math.max(0, Math.min(1, salesShareByYear[i] ?? 0));
    const delta =
      turnoverRate * (1 - prev) * salesShare;
    let next = prev + delta;
    next = Math.max(0, Math.min(1, next));
    stockElectrifiedByYear[i] = next;
    stockFossilByYear[i] = 1 - next;
  }
  return { stockElectrifiedByYear, stockFossilByYear };
}

/**
 * Build BAU pathway from raw buildings data and scenario.
 * From `baseYear` onward, residential/commercial gas inputs are held at base-year levels so absolute
 * therms/kWh evolve only with the stock model (matches smooth electrifiable-pool % charts). Without this,
 * year-to-year jumps in inventory/extrapolation can dwarf the modest first-year stock step (2023→2024).
 */
export function getBuildingsBauPathway(
  rawBuildingsData: BuildingsEnergyData,
  scenario: BuildingsBauScenario,
  options?: {
    baseYear?: number;
    targetYear?: number;
    turnoverRate?: number;
    initialStockElectrified?: number;
    cop?: number;
  }
): BuildingsBauPathway {
  const baseYear = options?.baseYear ?? 2023;
  const targetYear = options?.targetYear ?? 2045;
  const turnoverRate = options?.turnoverRate ?? turnoverRateForBauScenario(scenario);
  const initialStockElectrified = options?.initialStockElectrified ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR;
  const cop = options?.cop ?? HEAT_PUMP_COP;

  const years = Array.from(
    { length: targetYear - 2005 + 1 },
    (_, i) => 2005 + i
  );

  const baselineTherms = getBaselineThermsNoElectrification(rawBuildingsData, years);
  const baselineKwh = getBaselineTrendKwh(rawBuildingsData, years);

  // For years beyond data, hold last value (e.g. 2023) constant
  const lastTherms = baselineTherms[baselineTherms.length - 1] ?? baselineTherms[years.indexOf(2023)] ?? 0;
  const lastKwh = baselineKwh[baselineKwh.length - 1] ?? baselineKwh[years.indexOf(2023)] ?? 0;
  for (let i = 0; i < years.length; i++) {
    if (baselineTherms[i] === 0 && years[i] > 2023) baselineTherms[i] = lastTherms;
    if (baselineKwh[i] === 0 && years[i] > 2023) baselineKwh[i] = lastKwh;
  }

  const baseIdx = years.indexOf(baseYear);
  if (baseIdx >= 0) {
    const kAnchor = baselineKwh[baseIdx] ?? 0;
    if (kAnchor > 0) {
      for (let i = 0; i < years.length; i++) {
        if (years[i] >= baseYear) baselineKwh[i] = kAnchor;
      }
    }
  }

  const sectorGasForPathwayIndex = (i: number) => {
    const y = years[i];
    const queryYear = y < baseYear ? y : baseYear;
    return getNaturalGasConsumptionBySector(rawBuildingsData, queryYear);
  };

  const { res: salesRes, com: salesCom } = getSalesElectrificationShareByYearSplit(
    scenario,
    years,
    baseYear,
    targetYear
  );
  const { stockElectrifiedByYear: stockElectrifiedResidentialByYear } = evolveStock(
    salesRes,
    turnoverRate,
    initialStockElectrified,
    years,
    baseYear
  );
  const { stockElectrifiedByYear: stockElectrifiedCommercialByYear } = evolveStock(
    salesCom,
    turnoverRate,
    initialStockElectrified,
    years,
    baseYear
  );

  const { electrifiableResidential: eR, electrifiableCommercial: eC } = resolveBuildingEndUseAssumptions(null);

  const thermsByYear = years.map((_, i) => {
    const { resTherms, comTherms } = sectorGasForPathwayIndex(i);
    return (
      resTherms * (1 - eR * stockElectrifiedResidentialByYear[i]) +
      comTherms * (1 - eC * stockElectrifiedCommercialByYear[i])
    );
  });
  const thermsDisplacedByYear = years.map((_, i) => {
    const { resTherms, comTherms } = sectorGasForPathwayIndex(i);
    return (
      resTherms * eR * stockElectrifiedResidentialByYear[i] + comTherms * eC * stockElectrifiedCommercialByYear[i]
    );
  });
  const electrifiablePoolByYear = years.map((_, i) => {
    const { resTherms, comTherms } = sectorGasForPathwayIndex(i);
    return resTherms * eR + comTherms * eC;
  });
  const stockElectrifiedByYear = years.map((_, i) => {
    const pool = electrifiablePoolByYear[i];
    if (pool <= 1e-12) return 0;
    return Math.min(1, thermsDisplacedByYear[i] / pool);
  });
  const stockFossilByYear = stockElectrifiedByYear.map((s) => Math.max(0, Math.min(1, 1 - s)));

  const addedKwhByYear = thermsDisplacedByYear.map((td) => td * (KWH_PER_THERM / cop));
  const kwhByYear = years.map((_, i) => baselineKwh[i] + addedKwhByYear[i]);

  const rngShareByYear = getRngShareByYear(scenario, years, baseYear, targetYear);

  const salesShareByYear = getBlendedSalesShareForDebug(scenario, years, baseYear, targetYear);

  // Validation: per-sector stock monotonic and <= 1
  for (const arr of [stockElectrifiedResidentialByYear, stockElectrifiedCommercialByYear]) {
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] < arr[i - 1] - 1e-6) {
        // stockElectrified decreased unexpectedly
      }
      if (arr[i] > 1.0001) {
        // stockElectrified exceeded 1
      }
    }
  }

  return {
    years,
    thermsByYear,
    kwhByYear,
    rngShareByYear,
    demandAnchorYear: baseYear,
    stockElectrifiedByYear,
    stockFossilByYear,
    salesElectrificationShareByYear: salesShareByYear,
    stockElectrifiedResidentialByYear,
    stockElectrifiedCommercialByYear,
    electrifiableResidential: eR,
    electrifiableCommercial: eC,
  };
}

/**
 * Get therms for a given year from pathway (interpolate if needed).
 */
export function getPathwayTherms(pathway: BuildingsBauPathway, year: number): number {
  const i = pathway.years.indexOf(year);
  if (i >= 0) return pathway.thermsByYear[i];
  if (year <= pathway.years[0]) return pathway.thermsByYear[0];
  if (year >= pathway.years[pathway.years.length - 1])
    return pathway.thermsByYear[pathway.thermsByYear.length - 1];
  const iHi = pathway.years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return pathway.thermsByYear[0];
  const t = (year - pathway.years[iLo]) / (pathway.years[iHi] - pathway.years[iLo]);
  return pathway.thermsByYear[iLo] + t * (pathway.thermsByYear[iHi] - pathway.thermsByYear[iLo]);
}

/**
 * Get kWh for a given year from pathway.
 */
export function getPathwayKwh(pathway: BuildingsBauPathway, year: number): number {
  const i = pathway.years.indexOf(year);
  if (i >= 0) return pathway.kwhByYear[i];
  if (year <= pathway.years[0]) return pathway.kwhByYear[0];
  if (year >= pathway.years[pathway.years.length - 1])
    return pathway.kwhByYear[pathway.kwhByYear.length - 1];
  const iHi = pathway.years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return pathway.kwhByYear[0];
  const t = (year - pathway.years[iLo]) / (pathway.years[iHi] - pathway.years[iLo]);
  return pathway.kwhByYear[iLo] + t * (pathway.kwhByYear[iHi] - pathway.kwhByYear[iLo]);
}

/**
 * Get RNG share for a given year (0–1).
 */
export function getPathwayRngShare(pathway: BuildingsBauPathway, year: number): number {
  const i = pathway.years.indexOf(year);
  if (i >= 0) return pathway.rngShareByYear[i];
  if (year <= pathway.years[0]) return pathway.rngShareByYear[0];
  if (year >= pathway.years[pathway.years.length - 1])
    return pathway.rngShareByYear[pathway.rngShareByYear.length - 1];
  const iHi = pathway.years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return pathway.rngShareByYear[0];
  const t = (year - pathway.years[iLo]) / (pathway.years[iHi] - pathway.years[iLo]);
  return pathway.rngShareByYear[iLo] + t * (pathway.rngShareByYear[iHi] - pathway.rngShareByYear[iLo]);
}

/**
 * Flat pathway: 2023 energy scaled by population only (no state or local policy).
 * Used for cost views "BAU costs" and "Total costs" (cost vs flat baseline).
 */
export function getFlatBuildingsPathway(baseline: {
  buildingsBauPathway?: BuildingsBauPathway;
  populationData: Record<number, number>;
}): BuildingsBauPathway {
  const years: number[] = [];
  for (let y = 2023; y <= 2045; y++) years.push(y);
  const pop = baseline.populationData;
  const pop2023 = pop[2023] ?? Object.values(pop)[0] ?? 1;
  const pathway = baseline.buildingsBauPathway;
  const therms2023 = pathway ? getPathwayTherms(pathway, 2023) : 0;
  const kwh2023 = pathway ? getPathwayKwh(pathway, 2023) : 0;
  const thermsByYear = years.map((y) => therms2023 * ((pop[y] ?? pop2023) / pop2023));
  const kwhByYear = years.map((y) => kwh2023 * ((pop[y] ?? pop2023) / pop2023));
  const rngShareByYear = years.map(() => 0);
  return {
    years,
    thermsByYear,
    kwhByYear,
    rngShareByYear,
    demandAnchorYear: pathway?.demandAnchorYear ?? 2023,
  };
}

function interpolatePathwayScalar(years: number[], values: number[], year: number): number {
  if (!values.length) return 0;
  const i = years.indexOf(year);
  if (i >= 0) return values[i];
  if (year <= years[0]) return values[0];
  if (year >= years[years.length - 1]) return values[values.length - 1];
  const iHi = years.findIndex((y) => y >= year);
  const iLo = iHi - 1;
  if (iLo < 0) return values[0];
  const t = (year - years[iLo]) / (years[iHi] - years[iLo]);
  return values[iLo] + t * (values[iHi] - values[iLo]);
}

/**
 * Aggregate BAU share (0–1) of **electrifiable** building gas converted to electric by this year (national default end-use split).
 */
export function getPathwayStockElectrified(pathway: BuildingsBauPathway, year: number): number {
  const arr = pathway.stockElectrifiedByYear;
  if (!arr || arr.length === 0) return pathway.years?.length ? 0 : 0;
  return interpolatePathwayScalar(pathway.years, arr, year);
}

/**
 * Residential turnover stock (0–1): share of sector heating stock on electric track; gas impact = × electrifiableResidential.
 */
export function getPathwayStockElectrifiedResidential(pathway: BuildingsBauPathway, year: number): number {
  const arr = pathway.stockElectrifiedResidentialByYear;
  if (!arr?.length) return getPathwayStockElectrified(pathway, year);
  return interpolatePathwayScalar(pathway.years, arr, year);
}

/** Commercial counterpart of {@link getPathwayStockElectrifiedResidential}. */
export function getPathwayStockElectrifiedCommercial(pathway: BuildingsBauPathway, year: number): number {
  const arr = pathway.stockElectrifiedCommercialByYear;
  if (!arr?.length) return getPathwayStockElectrified(pathway, year);
  return interpolatePathwayScalar(pathway.years, arr, year);
}

export { NG_EMISSION_FACTOR_KG_PER_THERM };

/** One row of the Berkeley (or any location) BAU debug table */
export interface BuildingsBauDebugRow {
  scenario: BuildingsBauScenario | 'current_policy_slider_0' | 'current_policy_slider_100';
  stockElectrified2023: number;
  stockElectrified2030: number;
  stockElectrified2045: number;
  therms2045: number;
  kwh2045: number;
  rngShare2045: number;
  buildingEmissions2045MT: number;
}

/**
 * Build the Berkeley (or any location) BAU debug table.
 * Returns one row per BAU scenario plus current_policy with slider 0 and slider 100.
 * gPerKwh2045: grid carbon intensity in 2045 (g CO2e/kWh). Use 0 for clean grid.
 */
export function getBuildingsBauDebugTable(
  rawBuildingsData: BuildingsEnergyData,
  options?: { gPerKwh2045?: number }
): BuildingsBauDebugRow[] {
  const gPerKwh = options?.gPerKwh2045 ?? 0;
  const scenarios: BuildingsBauScenario[] = ['conservative', 'current_policy', 'accelerated'];
  const rows: BuildingsBauDebugRow[] = [];

  for (const scenario of scenarios) {
    const pathway = getBuildingsBauPathway(rawBuildingsData, scenario);
    const i2023 = pathway.years.indexOf(2023);
    const i2030 = pathway.years.indexOf(2030);
    const i2045 = pathway.years.indexOf(2045);
    const stock2023 = pathway.stockElectrifiedByYear?.[i2023] ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR;
    const stock2030 = pathway.stockElectrifiedByYear?.[i2030] ?? 0;
    const stock2045 = pathway.stockElectrifiedByYear?.[i2045] ?? 0;
    const therms2045 = pathway.thermsByYear[i2045] ?? 0;
    const kwh2045 = pathway.kwhByYear[i2045] ?? 0;
    const rng2045 = pathway.rngShareByYear[i2045] ?? 0;
    const ngMT = (therms2045 * (1 - rng2045) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
    const elecMT = (kwh2045 * gPerKwh) / 1_000_000;
    rows.push({
      scenario,
      stockElectrified2023: stock2023,
      stockElectrified2030: stock2030,
      stockElectrified2045: stock2045,
      therms2045,
      kwh2045,
      rngShare2045: rng2045,
      buildingEmissions2045MT: ngMT + elecMT,
    });
  }

  const pathwayCP = getBuildingsBauPathway(rawBuildingsData, 'current_policy');
  const i2045 = pathwayCP.years.indexOf(2045);
  const bauTherms = pathwayCP.thermsByYear[i2045] ?? 0;
  const bauKwh = pathwayCP.kwhByYear[i2045] ?? 0;
  const bauRng = pathwayCP.rngShareByYear[i2045] ?? 0;
  const bauNgMT = (bauTherms * (1 - bauRng) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
  const bauElecMT = (bauKwh * gPerKwh) / 1_000_000;
  rows.push({
    scenario: 'current_policy_slider_0',
    stockElectrified2023: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2023)] ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR,
    stockElectrified2030: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2030)] ?? 0,
    stockElectrified2045: pathwayCP.stockElectrifiedByYear?.[i2045] ?? 0,
    therms2045: bauTherms,
    kwh2045: bauKwh,
    rngShare2045: bauRng,
    buildingEmissions2045MT: bauNgMT + bauElecMT,
  });
  const { electrifiableResidential: eRd, electrifiableCommercial: eCd } = resolveBuildingEndUseAssumptions(null);
  const { resTherms: r45d, comTherms: c45d } = getNaturalGasConsumptionBySector(rawBuildingsData, 2045);
  const electrifiablePool2045 = r45d * eRd + c45d * eCd;
  const ngRemainingIfAllElectrifiableConverted = r45d * (1 - eRd) + c45d * (1 - eCd);
  const addedKwh100 = electrifiablePool2045 * (KWH_PER_THERM / HEAT_PUMP_COP);
  const totalKwh100 = bauKwh + addedKwh100;
  const elecMT100 = (totalKwh100 * gPerKwh) / 1_000_000;
  const ngMT100 =
    (ngRemainingIfAllElectrifiableConverted * (1 - bauRng) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
  rows.push({
    scenario: 'current_policy_slider_100',
    stockElectrified2023: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2023)] ?? INITIAL_STOCK_ELECTRIFIED_BASE_YEAR,
    stockElectrified2030: pathwayCP.stockElectrifiedByYear?.[pathwayCP.years.indexOf(2030)] ?? 0,
    stockElectrified2045: pathwayCP.stockElectrifiedByYear?.[i2045] ?? 0,
    therms2045: ngRemainingIfAllElectrifiableConverted,
    kwh2045: totalKwh100,
    rngShare2045: bauRng,
    buildingEmissions2045MT: ngMT100 + elecMT100,
  });
  return rows;
}
